import { format } from "date-fns";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import { getDownloadTreatmentReportCSVDetails, getTreatmentWorklist } from "../../../app/woundReportSlice";
import { DASHBOARD_TREAMENTOUTCOME_ASSESSMENT, LIST_VIEW_LIMIT } from "../../../constants";
import Spreadsheet from "../../../static/images/ExportSpredsheet.svg";
import { DataTable, Pagination } from "../../secondary";
import style from "./treatmentReportData.module.scss";
import { Button } from "../../primary";
import Loader from "../../../static/images/spinner.svg";
import { AVG_CHANGE_FREQUENCY_COL, AVG_RATE_OF_HEALING_COL, TREATMENT_REPORT_COLUMNS, TREATMENT_REPORT_HEADERS } from "./treatmentReportConfig";

export const TreatmentReportData = ({ tempPayload, selectedDivOption }) => {
  const dispatch = useDispatch();

  const isTreatmentReportCsvLoading = useSelector((state) => state.woundreport.isTreatmentReportCsvLoading);
  const isTreatmentReportLoading = useSelector((state) => state.woundreport.isTreatmentReportLoading);
  const treatmentWorklistData = useSelector((state) => state.woundreport.treatmentWorklistData);
  const treatmentReportCsv = useSelector((state) => state.woundreport.treatmentReportCsv);
  const customerName = useSelector((state) => state.user.customerName);

  const [activePage, setActivePage] = useState(1);
  const [offSet, setOffSet] = useState(0);
  const [sortType, setSortType] = useState("");
  const csvLink = useRef();

  useEffect(() => {
    setSortType("");
    setOffSet(0);
    setActivePage(1);
    const woundAssessmentPayload = { ...tempPayload, selectedOption: selectedDivOption };
    const listPayLoad = {};
    listPayLoad["url"] = DASHBOARD_TREAMENTOUTCOME_ASSESSMENT;
    listPayLoad["method"] = "Post";
    listPayLoad["payload"] = woundAssessmentPayload;
    listPayLoad["queryParam"] = { offSet: 0, limit: LIST_VIEW_LIMIT };
    if (woundAssessmentPayload && woundAssessmentPayload.facility && woundAssessmentPayload.facility.length > 0) {
      dispatch(getTreatmentWorklist(listPayLoad));
    }
  }, [tempPayload, selectedDivOption]);

  const getFileName = () => {
    const csvDownloadDate = format(new Date(), "MM.dd.yyyy-hh.mm.aaa");
    return `${customerName}.${selectedDivOption}.Treatment_Outcomes.${csvDownloadDate}.csv`;
  };

  const isDressings = selectedDivOption === "Primary Dressings" || selectedDivOption === "Secondary Dressings";

  const columns = [...TREATMENT_REPORT_COLUMNS, ...(isDressings ? [AVG_CHANGE_FREQUENCY_COL] : []), AVG_RATE_OF_HEALING_COL];

  const handleSort = useCallback(
    (data) => {
      if (data && data.sortBy[0] && data.sortBy[0].id) {
        const woundAssessmentPayload = { ...tempPayload, selectedOption: selectedDivOption };
        const sortingColumn = data.sortBy[0];
        const sortingType = sortingColumn ? (sortingColumn.desc ? ":DESC" : ":ASC") : "";
        let sortParam = sortingColumn.id + sortingType;
        const listPayLoad = {};
        listPayLoad["url"] = DASHBOARD_TREAMENTOUTCOME_ASSESSMENT;
        listPayLoad["method"] = "Post";
        listPayLoad["payload"] = woundAssessmentPayload;
        listPayLoad["queryParam"] = { offSet: isNaN(offSet) === true ? 0 : offSet, limit: LIST_VIEW_LIMIT, sort: sortParam };
        setSortType(sortParam);
        if (woundAssessmentPayload && woundAssessmentPayload.facility && woundAssessmentPayload.facility.length > 0) {
          dispatch(getTreatmentWorklist(listPayLoad));
        }
      }
    },
    [dispatch, tempPayload, selectedDivOption, offSet]
  );
  const getActivePage = (data) => {
    setActivePage(data);
    const woundAssessmentPayload = { ...tempPayload, selectedOption: selectedDivOption };
    const listPayLoad = {};
    listPayLoad["url"] = DASHBOARD_TREAMENTOUTCOME_ASSESSMENT;
    listPayLoad["method"] = "Post";
    listPayLoad["payload"] = woundAssessmentPayload;
    listPayLoad["queryParam"] = {
      limit: LIST_VIEW_LIMIT,
      offSet: data * LIST_VIEW_LIMIT - LIST_VIEW_LIMIT < LIST_VIEW_LIMIT ? 0 : data * LIST_VIEW_LIMIT - LIST_VIEW_LIMIT,
      sort: sortType ? sortType : "",
    };
    setOffSet(data * LIST_VIEW_LIMIT - LIST_VIEW_LIMIT < LIST_VIEW_LIMIT ? 0 : data * LIST_VIEW_LIMIT - LIST_VIEW_LIMIT);
    if (woundAssessmentPayload && woundAssessmentPayload.facility && woundAssessmentPayload.facility.length > 0) {
      dispatch(getTreatmentWorklist(listPayLoad));
    }
  };

  const handleDownloadCSV = async () => {
    const total = treatmentWorklistData?.metadata?.total ?? 0;
    if (!total) return;

    const queryParam = `limit=${total}&offSet=0`;

    const payload = {
      ...tempPayload,
      selectedOption: selectedDivOption,
    };

    if (!payload?.facility?.length) return;

    await dispatch(getDownloadTreatmentReportCSVDetails(payload, queryParam));

    setTimeout(() => {
      csvLink.current?.link?.click();
    }, 0);
  };

  return (
    <>
      {treatmentReportCsv?.treatmentOutcome && treatmentReportCsv?.treatmentOutcome.length > 0 && (
        <>
          <div className="col-sm-12 mb-2 justify-content-end d-flex">
            <span className={style.down_spread}>
              <Button type="button" className={style.download_btn} onClick={handleDownloadCSV}>
                {isTreatmentReportCsvLoading ? (
                  <img src={Loader} alt="loading" style={{ width: "2rem", height: "2rem", paddingBottom: "4px" }} />
                ) : (
                  <img src={Spreadsheet} alt="" />
                )}{" "}
                Download as CSV
              </Button>
              <CSVLink
                headers={TREATMENT_REPORT_HEADERS}
                data={treatmentReportCsv?.treatmentOutcome ?? []}
                filename={getFileName()}
                target="_blank"
                ref={csvLink}
                style={{ display: "none" }}
              />
            </span>
          </div>
        </>
      )}
      {isTreatmentReportLoading ? (
        <div className="loader">
          <img src={Loader} alt="loading" />
        </div>
      ) : (
        <div className="col-12">
          <DataTable
            columns={columns}
            data={treatmentWorklistData?.treatmentOutcome ?? []}
            sorting={true}
            setSelectedRows={() => {}}
            onSort={handleSort}
            detailView={true}
            deleteDataList={() => {}}
            showFixedColumn={false}
            workListView={true}
            reportView={true}
            removeCursor={true}
            sortType={sortType ? sortType : ""}
          />
        </div>
      )}
      {!isTreatmentReportLoading && Array.isArray(treatmentWorklistData.treatmentOutcome) && treatmentWorklistData.treatmentOutcome.length > 1 && (
        <Pagination
          activePage={activePage}
          itemsCountPerPage={treatmentWorklistData?.metadata ? treatmentWorklistData?.metadata?.limit : 50}
          totalItemsCount={treatmentWorklistData?.metadata ? treatmentWorklistData?.metadata?.total : 0}
          getActivePage={getActivePage}
          reportView={true}
        />
      )}
    </>
  );
};
